import React, { FC } from 'react';
import {
    TextField, FormControl,
    InputLabel, Select, MenuItem,
    Typography, Box, IconButton, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { TipoCalificadorEnum } from 'repository/timekeeper/@types/TypeCalificador';
import { ComparadorEnum } from 'repository/timekeeper/@types/TypeComparador';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';
import { Paycode } from 'repository/timekeeper/@types/PaycodeModel';
import { CalificadorCreate, Calificador } from 'repository/timekeeper/@types/Calificador';

interface QualifiersListProps {
    qualifiers: CalificadorCreate[];
    setQualifiers: (value: CalificadorCreate[]) => void;
    paycodes: Paycode[];
}

export const QualifiersList: FC<QualifiersListProps> = ({
    qualifiers,
    setQualifiers,
    paycodes
}) => {
    const { t } = useTranslation();

    const handleAddQualifier = () => {
        setQualifiers([
            ...qualifiers,
            {
                qualifier_type: TipoCalificadorEnum.NINGUNO,
                qualifier_paycode_id: null,
                qualifier_comparator: ComparadorEnum.MAYOR_IGUAL_QUE,
                period_amount: 1,
                period_unit_time: FrequencyUnitTime.DIAS,
                qualifier_amount: 1,
                interval_hour_amount: 1,
                qualifier_grant_id: null
            } as Calificador
        ]);
    };

    const handleRemoveQualifier = (index: number) => {
        setQualifiers(qualifiers.filter((_, i) => i !== index));
    };

    const handleQualifierChange = (index: number, field: string, value: any) => {
        console.log('Updating qualifier at index', index, 'field', field, 'to value', value);
        const updated = [...qualifiers];
        updated[index] = { ...updated[index], [field]: value };
        setQualifiers(updated);
    };

    return (
        <>
            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2">
                        {t('accruals.grants.dialog.fields.rater')}
                    </Typography>
                    <IconButton onClick={handleAddQualifier} size="small" color="primary">
                        <AddIcon />
                    </IconButton>
                </Box>
            </Grid>

            {qualifiers.map((qualifier, index) => (
                <Grid key={index} size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1 }}>
                    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} position="relative">
                        <IconButton
                            onClick={() => handleRemoveQualifier(index)}
                            size="small"
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <DeleteIcon />
                        </IconButton>
                        <Grid container spacing={1}>
                            <Grid size={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('accruals.grants.dialog.fields.rater')}</InputLabel>
                                    <Select
                                        value={qualifier.qualifier_type}
                                        onChange={(e) => handleQualifierChange(index, 'qualifier_type', e.target.value)}
                                        label={t('accruals.grants.dialog.fields.rater')}
                                    >
                                        <MenuItem value={TipoCalificadorEnum.NINGUNO}>{t('accruals.grants.dialog.fields.rater_options.none_rater')}</MenuItem>
                                        <MenuItem value={TipoCalificadorEnum.DIAS_ACTIVO}>{t('accruals.grants.dialog.fields.rater_options.active_days_rater')}</MenuItem>
                                        <MenuItem value={TipoCalificadorEnum.DIAS_TRABAJADOS}>{t('accruals.grants.dialog.fields.rater_options.worked_days_rater')}</MenuItem>
                                        <MenuItem value={TipoCalificadorEnum.HORAS_CODIGO_DE_PAGO}>{t('accruals.grants.dialog.fields.rater_options.hours_paycodes')}</MenuItem>
                                        <MenuItem value={TipoCalificadorEnum.DIAS_COMO_EMPLEADO}>{t('accruals.grants.dialog.fields.rater_options.days_like_employee')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}>
                                <FormControl fullWidth size="small" disabled={qualifier.qualifier_type !== TipoCalificadorEnum.HORAS_CODIGO_DE_PAGO}>
                                    <InputLabel>{t('accruals.grants.dialog.fields.amount')}</InputLabel>
                                    <Select
                                        value={'qualifier_comparator' in qualifier ? qualifier.qualifier_comparator : ComparadorEnum.MAYOR_IGUAL_QUE}
                                        onChange={(e) => handleQualifierChange(index, 'qualifier_comparator', e.target.value)}
                                        defaultValue={ComparadorEnum.MAYOR_IGUAL_QUE}
                                        label={t('accruals.grants.dialog.fields.amount')}
                                    >
                                        <MenuItem value={ComparadorEnum.MAYOR_IGUAL_QUE}>{'>='}</MenuItem>
                                        <MenuItem value={ComparadorEnum.MENOR_IGUAL_QUE}>{'<='}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {/* Cantidad: disponible para todos los tipos */}
                            <Grid size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={t('accruals.grants.dialog.fields.amount')}
                                    value={qualifier.qualifier_amount || 0}
                                    onChange={(e) => handleQualifierChange(index, 'qualifier_amount', Number(e.target.value))}
                                    slotProps={{ htmlInput: { min: 0 } }}
                                />
                            </Grid>
                            {/* Código de pago */}
                            <Grid size={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }}>
                                <FormControl fullWidth size="small" disabled={qualifier.qualifier_type !== TipoCalificadorEnum.HORAS_CODIGO_DE_PAGO}>
                                    <InputLabel>{t('accruals.grants.dialog.fields.paycode')}</InputLabel>
                                    <Select
                                        value={qualifier.qualifier_type === TipoCalificadorEnum.HORAS_CODIGO_DE_PAGO ? qualifier.qualifier_paycode_id || '' : ''}
                                        onChange={(e) => handleQualifierChange(index, 'qualifier_paycode_id', e.target.value)}
                                        label={t('accruals.grants.dialog.fields.paycode')}
                                    >
                                        {paycodes.map((paycode) => (
                                            <MenuItem key={paycode.paycode_id} value={paycode.paycode_id}>
                                                {paycode.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Período a comprobar: disponible para todos los tipos */}
                            <Grid size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={t('accruals.grants.dialog.fields.period_to_check')}
                                    value={(qualifier as any).period_amount || 1}
                                    onChange={(e) => handleQualifierChange(index, 'period_amount', Number(e.target.value))}
                                    slotProps={{
                                        htmlInput: {
                                            min: 1,
                                            title: t('accruals.grants.dialog.fields.period_to_check'), 'aria-label': t('accruals.grants.dialog.fields.period_to_check')
                                        },
                                        inputLabel: { shrink: true }
                                    }}
                                />
                            </Grid>

                            {/* Frecuencia */}
                            <Grid size={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('accruals.grants.dialog.fields.period_to_check')}</InputLabel>
                                    <Select
                                        value={'period_unit_time' in qualifier ? qualifier.period_unit_time : FrequencyUnitTime.DIAS}
                                        onChange={(e) => handleQualifierChange(index, 'period_unit_time', e.target.value)}
                                        label={t('accruals.grants.dialog.fields.period_to_check')}
                                    >
                                        <MenuItem value={FrequencyUnitTime.DIAS}>{t('accruals.grants.dialog.fields.frequency_options.DAYS')}</MenuItem>
                                        <MenuItem value={FrequencyUnitTime.SEMANAS}>{t('accruals.grants.dialog.fields.frequency_options.WEEKS')}</MenuItem>
                                        <MenuItem value={FrequencyUnitTime.MESES}>{t('accruals.grants.dialog.fields.frequency_options.MONTHS')}</MenuItem>
                                        <MenuItem value={FrequencyUnitTime.ANOS}>{t('accruals.grants.dialog.fields.frequency_options.YEARS')}</MenuItem>
                                        <MenuItem value={FrequencyUnitTime.PERIODO_PAGO}>{t('accruals.grants.dialog.fields.frequency_options.PERIOD_PAY')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {/* Intervalo total de horas */}
                            <Grid size={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={t('accruals.grants.dialog.fields.interval_hours_total')}
                                    value={qualifier.interval_hour_amount || 1}
                                    onChange={(e) => handleQualifierChange(index, 'interval_hour_amount', Number(e.target.value))}
                                    slotProps={{
                                        htmlInput: { min: 1, title: t('accruals.grants.dialog.fields.interval_hours_total'), 'aria-label': t('accruals.grants.dialog.fields.interval_hours_total') },
                                        inputLabel: { shrink: true }
                                    }}
                                />
                            </Grid>
                            {/* Unidad */}
                            <Grid size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}>
                                <FormControl fullWidth size="small" disabled={true}>
                                    <InputLabel>{t('accruals.grants.dialog.fields.period')}</InputLabel>
                                    {/* <Select
                                        value={FrequencyUnitTime.DIAS}
                                        label={t('accruals.grants.dialog.fields.unit')}
                                    >
                                        <MenuItem value={FrequencyUnitTime.DIAS}>{t('accruals.grants.dialog.fields.frequency_options.DAYS')}</MenuItem>
                                        <MenuItem value={FrequencyUnitTime.SEMANAS}>{t('accruals.grants.dialog.fields.frequency_options.WEEKS')}</MenuItem>
                                        <MenuItem value={FrequencyUnitTime.MESES}>{t('accruals.grants.dialog.fields.frequency_options.MONTHS')}</MenuItem>
                                        <MenuItem value={FrequencyUnitTime.ANOS}>{t('accruals.grants.dialog.fields.frequency_options.YEARS')}</MenuItem>
                                        <MenuItem value={FrequencyUnitTime.PERIODO_PAGO}>{t('accruals.grants.dialog.fields.frequency_options.PERIOD_PAY')}</MenuItem>
                                    </Select> */}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            ))}
        </>
    );
};
